import React, { FC, useRef, useState } from 'react';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';
import Loadable from '@app/utils/Loadable';
import {
  DataTable,
  TableScrollContainer,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EvidenceCell,
} from '../NewVerificationTab.styles';
import { TableColumn, ExpandableTableRow, DataPointType } from '../NewVerificationTab.data';
import { EvidenceDisplay } from './EvidenceDisplay';
import { EditModal } from './EditModal';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { DataPointCellContent } from './DataPointCellContent';
import { AutoMatchCellContent } from './AutoMatchCellContent';
import { ReviewCellContent } from './ReviewCellContent';
import { StatusIcon } from './StatusIcon';
import { createRowIdResolver } from './rowIdentity';
import {
  extractVendorValue,
  extractEvidenceValue,
  cellContentToString,
  isEmptyValue,
  computeCellState,
  getCellBgColor,
  canAddChildRow,
  buildVendorPresetItems,
  parseLegalIdentifierString,
  resolveReviewDisplayValue,
} from '../NewVerificationTab.utils';
import { COLUMN_KEYS, NOT_RELEVANT } from '../NewVerificationTab.constants';

interface GenericTableProps {
  columns: TableColumn[];
  rows: ExpandableTableRow[];
  onRowUpdate?: (rowIndex: number, columnKey: string, newValue: React.ReactNode) => void;
  onAddSubRow?: (rowIndex: number) => void;
  onDeleteRow?: (rowIndex: number) => void;
  taxIdTypes?: Loadable<LegalEntityIdType[]>;
  /** Country codes for which a type (by its id) is configured as a Semi-Strong LEI. */
  semiStrongCountryCodesByTypeId?: Record<string, string[]>;
  /** If true, user can edit Manual Processing column (EDITOR or APPROVER role) */
  canEditManualProcess?: boolean;
  /** If true, user can edit Final Review column (APPROVER role only) */
  canEditFinalReview?: boolean;
}

interface EditModalState {
  isOpen: boolean;
  columnType: 'manualProcess' | 'finalReview' | null;
  /** Stable id of the row being edited — survives add/delete row reordering (unlike a positional index). */
  rowId: number | null;
  currentValue: string;
  dataPointType: DataPointType | null;
  dataPoint?: React.ReactNode;
  vendorValue?: string;
  evidenceValue?: string;
  evidenceItems?: Array<{ value: string; source?: string; evidenceType?: string }>;
  vendorItems?: Array<{ value: string; source?: string; evidenceType?: string }>;
  inputBackgroundColor?: string;
  /** True when the row's Auto Matching result is NotRelevant — lets the modal default the checkbox. */
  autoMatchIsNotRelevant?: boolean;
}

const CLOSED_MODAL: EditModalState = {
  isOpen: false,
  columnType: null,
  rowId: null,
  currentValue: '',
  dataPointType: null,
  vendorValue: undefined,
  evidenceValue: undefined,
  evidenceItems: undefined,
  vendorItems: undefined,
  inputBackgroundColor: undefined,
  autoMatchIsNotRelevant: undefined,
};

export const GenericTable: FC<GenericTableProps> = ({
  columns,
  rows,
  onRowUpdate,
  onAddSubRow,
  onDeleteRow,
  taxIdTypes,
  semiStrongCountryCodesByTypeId,
  canEditManualProcess = true,
  canEditFinalReview = true,
}) => {
  // Row-scoped UI state (expanded / session-edited highlight / edit-modal target) must be keyed by a
  // STABLE id, not the array index: handleAddSubRow/handleDeleteRow splice the rows array and shift
  // every later index, so index-keyed state would follow the position and land on the wrong row after
  // an add/delete. createRowIdResolver assigns each row a permanent id that survives both reordering
  // and the spread-copy an edit produces (see rowIdentity.ts).
  const rowIdRef = useRef(createRowIdResolver());
  const rowId = rowIdRef.current;

  // Flattened LEI type list (resolved), used to turn a formatted legal-identifier cell value back
  // into its typeId so we can look it up in semiStrongCountryCodesByTypeId. Empty while loading.
  const resolvedTaxIdTypes = taxIdTypes?.resolve(
    (types) => types,
    () => [],
  );

  // Whether a Manual/Final legal-identifier cell value is configured as a Semi-Strong LEI: parse the
  // formatted "value - type (country)" cell, resolve its typeId, and check whether the selected
  // country is listed for that type. Only legal-identifier rows with an actual value qualify.
  //
  // Uses resolveReviewDisplayValue (not the raw column) so the shield tracks the value actually
  // PRESENTED in the cell: the Final Review column falls back to the Manual value when Final is empty,
  // and the shield should show for that presented value even though nothing was saved on Final yet.
  const isReviewCellSemiStrong = (row: ExpandableTableRow, colKey: string): boolean => {
    if (row.dataPointType !== 'legalIdentifiers') return false;
    // A cell explicitly marked Not relevant presents no value — no shield.
    if (cellContentToString(row[colKey as keyof ExpandableTableRow] as React.ReactNode) === NOT_RELEVANT) return false;
    const raw = cellContentToString(resolveReviewDisplayValue(row, colKey));
    if (!raw || isEmptyValue(raw) || raw === NOT_RELEVANT) return false;
    const parsed = parseLegalIdentifierString(raw, resolvedTaxIdTypes ?? []);
    if (!parsed?.typeId || !parsed.countryCode) return false;
    return !!semiStrongCountryCodesByTypeId?.[parsed.typeId]?.includes(parsed.countryCode);
  };

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sessionEdits, setSessionEdits] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [editModal, setEditModal] = useState<EditModalState>(CLOSED_MODAL);

  const toggleRow = (id: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const recordSessionEdit = (id: number | null, columnType: string | null): void => {
    if (id !== null && (columnType === COLUMN_KEYS.MANUAL_PROCESS || columnType === COLUMN_KEYS.FINAL_REVIEW)) {
      setSessionEdits((prev) => new Set(prev).add(`${id}-${columnType}`));
    }
  };

  const handleEditClick = (
    columnType: 'manualProcess' | 'finalReview',
    rowIndex: number,
    currentValue: React.ReactNode,
    dataPointType: DataPointType,
  ): void => {
    if (columnType === 'manualProcess' && !canEditManualProcess) return;
    if (columnType === 'finalReview' && !canEditFinalReview) return;

    const row = rows[rowIndex];
    const vendorValue = extractVendorValue(row);
    const evidenceValue = extractEvidenceValue(row);
    const evidenceItems = Array.isArray(row.evidence) ? row.evidence : undefined;
    const vendorItems = buildVendorPresetItems(row.vendor);

    // Final Review inherits the Manual Processing value when it has none — mirror that in the
    // dialog so the input is pre-filled with what the (inherited) cell already displays. Only
    // seed when Final Review is genuinely blank, not when it was explicitly marked Not relevant.
    const currentValueString = cellContentToString(currentValue);
    const seededValue =
      columnType === COLUMN_KEYS.FINAL_REVIEW && isEmptyValue(currentValueString) && currentValueString !== NOT_RELEVANT
        ? cellContentToString(row.manualProcess)
        : currentValueString;

    // In the Final Review dialog, tint the input to match the Final Review cell's background so
    // the dialog carries the same status indication (match / edited / not relevant / …) the cell shows.
    const finalReviewCellColor =
      columnType === COLUMN_KEYS.FINAL_REVIEW
        ? getCellBgColor(computeCellState(row, COLUMN_KEYS.FINAL_REVIEW, sessionEdits, rowId(row)), COLUMN_KEYS.FINAL_REVIEW)
        : undefined;

    setEditModal({
      isOpen: true,
      columnType,
      rowId: rowId(row),
      currentValue: seededValue,
      dataPointType,
      dataPoint: row.dataPoint,
      vendorValue: !isEmptyValue(vendorValue) ? vendorValue : undefined,
      evidenceValue: !isEmptyValue(evidenceValue) ? evidenceValue : undefined,
      evidenceItems: evidenceItems && evidenceItems.length > 0 ? evidenceItems : undefined,
      vendorItems: vendorItems.length > 0 ? vendorItems : undefined,
      inputBackgroundColor: finalReviewCellColor,
      autoMatchIsNotRelevant: row.automatedMatching === 'NotRelevant',
    });
  };

  const commitEdit = (value: string | undefined): void => {
    // Resolve the stored stable rowId back to the current array index at commit time — if rows were
    // added/removed while the modal was open, the index the parent needs may have shifted.
    const targetIndex = editModal.rowId !== null ? rows.findIndex((row) => rowId(row) === editModal.rowId) : -1;
    if (targetIndex !== -1 && editModal.rowId !== null && editModal.columnType && onRowUpdate) {
      onRowUpdate(targetIndex, editModal.columnType, value === undefined ? NOT_RELEVANT : value);
      recordSessionEdit(editModal.rowId, editModal.columnType);
    }
    setEditModal(CLOSED_MODAL);
  };

  const renderMainRow = (row: ExpandableTableRow, index: number): React.ReactNode => {
    const id = rowId(row);
    const isExpanded = expandedRows.has(id);
    const isParentRow = !row.isChildRow;
    const nextRow = rows[index + 1];
    const hasChildRowFollowing = !!(nextRow && nextRow.isChildRow);
    const hasExpandedSubRows = !!(row.expandable && isExpanded && row.subRows?.length);
    const isLastInGroup = row.isChildRow ? !nextRow || !nextRow.isChildRow : !hasChildRowFollowing && !hasExpandedSubRows;

    return (
      <TableRow
        key={id}
        data-is-parent={isParentRow}
        data-is-child={row.isChildRow}
        data-has-child-following={hasChildRowFollowing || hasExpandedSubRows}
        data-is-last-in-group={isLastInGroup}
      >
        {columns.map((col) => {
          const cellContent = row[col.key as keyof ExpandableTableRow] as React.ReactNode;
          const state = computeCellState(row, col.key, sessionEdits, id);
          const bgColor = getCellBgColor(state, col.key);

          return (
            <TableCell
              key={col.key}
              data-column={col.key}
              data-is-child={row.isChildRow}
              data-has-child-following={hasChildRowFollowing || hasExpandedSubRows}
              data-is-last-in-group={isLastInGroup}
              style={bgColor ? { backgroundColor: bgColor } : undefined}
            >
              {col.key === COLUMN_KEYS.DATA_POINT ? (
                <DataPointCellContent
                  label={cellContent}
                  isParentRow={isParentRow}
                  isExpanded={isExpanded}
                  expandable={row.expandable}
                  showDeleteIcon={row.showDeleteIcon}
                  showAddIcon={row.showAddIcon && !!row.dataPointType && canAddChildRow(rows, index, row.dataPointType)}
                  onToggleExpand={(): void => toggleRow(id)}
                  onDelete={(): void => {
                    setRowToDelete(index);
                    setDeleteConfirmOpen(true);
                  }}
                  onAdd={(): void => onAddSubRow?.(index)}
                />
              ) : col.key === COLUMN_KEYS.EVIDENCE ? (
                <EvidenceCell>
                  <EvidenceDisplay evidence={row[col.key as keyof ExpandableTableRow] as any} />
                </EvidenceCell>
              ) : col.key === COLUMN_KEYS.AUTOMATED_MATCHING ? (
                <AutoMatchCellContent row={row} state={state} />
              ) : col.key === COLUMN_KEYS.MANUAL_PROCESS || col.key === COLUMN_KEYS.FINAL_REVIEW ? (
                <ReviewCellContent
                  row={row}
                  colKey={col.key}
                  state={state}
                  rowIndex={index}
                  showSemiStrongShield={isReviewCellSemiStrong(row, col.key)}
                  canEdit={
                    (col.key === COLUMN_KEYS.MANUAL_PROCESS && canEditManualProcess) ||
                    (col.key === COLUMN_KEYS.FINAL_REVIEW && canEditFinalReview)
                  }
                  onEditClick={handleEditClick}
                />
              ) : col.key === COLUMN_KEYS.VENDOR ? (
                <EvidenceCell>
                  <EvidenceDisplay evidence={row[col.key as keyof ExpandableTableRow] as any} />
                </EvidenceCell>
              ) : (
                cellContent
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  const renderSubRow = (
    subRow: Record<string, React.ReactNode>,
    parentIndex: number,
    subIndex: number,
    isLastSubRow: boolean,
  ): React.ReactNode => (
    <TableRow key={`${parentIndex}-${subIndex}`} data-is-child={true} data-is-last-sub-row={isLastSubRow}>
      {columns.map((col) => {
        const subCellContent = subRow[col.key] as React.ReactNode;
        const subStatusType = typeof subCellContent === 'string' ? subCellContent : null;
        return (
          <TableCell
            key={col.key}
            data-column={col.key}
            data-is-child={true}
            data-is-last-sub-row={isLastSubRow}
            style={col.key !== COLUMN_KEYS.DATA_POINT ? { paddingLeft: '48px' } : undefined}
          >
            {col.key === COLUMN_KEYS.EVIDENCE ? (
              <EvidenceCell>
                <EvidenceDisplay evidence={subRow[col.key] as any} />
              </EvidenceCell>
            ) : col.key === COLUMN_KEYS.AUTOMATED_MATCHING ? (
              <StatusIcon status={subStatusType as any} variant='automated' />
            ) : col.key === COLUMN_KEYS.VENDOR ? (
              subCellContent || '-'
            ) : (
              subCellContent || ''
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );

  const renderRows = (): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    rows.forEach((row, index) => {
      result.push(renderMainRow(row, index));
      if (row.expandable && expandedRows.has(rowId(row)) && row.subRows) {
        row.subRows.forEach((subRow, subIndex) => {
          result.push(
            renderSubRow(subRow as Record<string, React.ReactNode>, index, subIndex, subIndex === row.subRows!.length - 1),
          );
        });
      }
    });
    return result;
  };

  return (
    <>
      <TableScrollContainer>
        <DataTable>
          <TableHeader>
            <tr>
              {columns.map((col) => (
                <TableHeaderCell key={col.key} data-column={col.key}>
                  {col.title}
                </TableHeaderCell>
              ))}
            </tr>
          </TableHeader>
          <TableBody>{renderRows()}</TableBody>
        </DataTable>
      </TableScrollContainer>

      <EditModal
        isOpen={editModal.isOpen}
        currentValue={editModal.currentValue}
        dataPointType={editModal.dataPointType}
        dataPoint={editModal.dataPoint}
        vendorValue={editModal.vendorValue}
        evidenceValue={editModal.evidenceValue}
        evidenceItems={editModal.evidenceItems}
        vendorItems={editModal.vendorItems}
        columnType={editModal.columnType}
        inputBackgroundColor={editModal.inputBackgroundColor}
        autoMatchIsNotRelevant={editModal.autoMatchIsNotRelevant}
        taxIdTypes={taxIdTypes}
        semiStrongCountryCodesByTypeId={semiStrongCountryCodesByTypeId}
        onSave={commitEdit}
        onCancel={(): void => setEditModal(CLOSED_MODAL)}
      />

      <DeleteConfirmDialog
        isOpen={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={(): void => {
          if (rowToDelete !== null) onDeleteRow?.(rowToDelete);
          setDeleteConfirmOpen(false);
          setRowToDelete(null);
        }}
        onCancel={(): void => {
          setDeleteConfirmOpen(false);
          setRowToDelete(null);
        }}
        type='row'
        id='delete-row-confirm'
      />
    </>
  );
};
